import { useMemo, useState } from 'react'
import * as React from 'react'
import { useSnackbar } from 'notistack'
import styled from 'styled-components'
import {
  NewsItemHeader_NewsItemFragment,
  SharePlatFormType,
  useCurrentUserQuery,
  useIncrementShareCountMutation,
} from 'generated/graphql'
import NewsItemModifyModal from '@app.components/modals/NewsItemModifyModal'
import AlreadyReportedModal from '@app.feature/channel/component/modals/AlreadyReportedModal'
import DeleteNewsItemModal from '@app.feature/channel/component/modals/DeleteNewsItemModal'
import DesktopOnlyModal from '@app.feature/channel/component/modals/DesktopOnlyModal'
import ReportModal from '@app.feature/channel/component/modals/ReportModal'
import useChannelError from '@app.feature/channel/hook/useChannelError'
import useDropdown from '@app.modules/hooks/useDropdown'
import useIsMobile from '@app.modules/hooks/useIsMobile'
import MoreDropdownBtn from 'app.feature/channel/component/MoreDropdownBtn'
import LoginAlert from 'components/LoginAlert'

type TProps = {
  data: NewsItemHeader_NewsItemFragment
}

const DropdownBtn: React.FC<TProps> = ({ data }) => {
  const currentUserQuery = useCurrentUserQuery()
  const [menuRef, isDropdownOpened, handleDropDownBtn] = useDropdown()
  const [reportModalOpen, setReportModalOpen] = useState(false)
  const [modifyModalOpen, setModifyModalOpen] = useState(false)
  const [deleteNewsItemModalOpen, setDeleteNewsItemModalOpen] = useState(false)
  const [loginModalOpen, setLoginModalOpen] = useState(false)
  const [desktopOnlyModalOpen, setDesktopOnlyModalOpen] = useState(false)
  const isMobile = useIsMobile()
  const { enqueueSnackbar } = useSnackbar()
  const onError = useChannelError()
  const [incrementShareCountMutate] = useIncrementShareCountMutation({})

  const incrementShareCountWithPlatform = (platform: SharePlatFormType) => {
    if (!data.id) return
    incrementShareCountMutate({
      variables: {
        newsItemId: data.id,
        platform,
      },
    })
  }

  const newsItemUrl = useMemo(() => {
    if (typeof window !== 'object') return ''
    return `${window.location.origin}/channel/${data.channel?.url}/${data.id}`
  }, [data])

  const handleCopyMenuClick = () => {
    if (onError()) return
    navigator.clipboard.writeText(newsItemUrl)
    incrementShareCountWithPlatform(SharePlatFormType.URL)
    enqueueSnackbar('복사완료')
  }

  const menus = useMemo<
    {
      label: string
      handleClick: () => void
    }[]
  >(() => {
    const menus: {
      label: string
      handleClick: () => void
    }[] = []

    // 주소 복사
    menus.push({
      label: '주소 복사',
      handleClick: handleCopyMenuClick,
    })

    // 신고
    if (!data.viewerIsAuthor) {
      menus.push({
        label: '소식 신고',
        handleClick: () => {
          if (onError()) return
          if (!currentUserQuery.data?.currentUser)
            return setLoginModalOpen(true)
          setReportModalOpen(true)
        },
      })
    }

    // 수정
    if (data.viewerIsAuthor) {
      menus.push({
        label: '소식 수정',
        handleClick: () => {
          if (onError()) return
          if (isMobile) setDesktopOnlyModalOpen(true)
          else setModifyModalOpen(true)
        },
      })
    }

    // 삭제
    if (data.viewerCanDelete) {
      menus.push({
        label: '소식 삭제',
        handleClick: () => {
          if (onError()) return
          setDeleteNewsItemModalOpen(true)
        },
      })
    }

    return menus
  }, [data, currentUserQuery.data])

  return (
    <StyledWrapper>
      <div
        ref={menuRef}
        onClick={handleDropDownBtn}
        data-open={isDropdownOpened}
      >
        <MoreDropdownBtn className="more-menu-btn" />
      </div>
      <div className="more-menu-list" data-open={isDropdownOpened}>
        {menus.map((menu, idx) => (
          <button className="more-menu" key={idx} onClick={menu.handleClick}>
            {menu.label}
          </button>
        ))}
      </div>
      {data.viewerHasReported && (
        <AlreadyReportedModal
          type="newsItem"
          open={reportModalOpen}
          onClose={() => setReportModalOpen(false)}
        />
      )}
      {!data.viewerHasReported && (
        <ReportModal
          type="newsItem"
          open={reportModalOpen}
          reportedId={data.id}
          onClose={() => setReportModalOpen(false)}
        />
      )}
      {modifyModalOpen && (
        <NewsItemModifyModal
          newsItemId={data.id}
          onClose={() => setModifyModalOpen(false)}
        />
      )}
      <DeleteNewsItemModal
        open={deleteNewsItemModalOpen}
        newsItemId={data.id}
        onClose={() => setDeleteNewsItemModalOpen(false)}
      />
      <LoginAlert
        DialogProps={{
          open: loginModalOpen,
        }}
        onRequestClose={() => setLoginModalOpen(false)}
      />
      <DesktopOnlyModal
        open={desktopOnlyModalOpen}
        onClose={() => setDesktopOnlyModalOpen(false)}
      />
    </StyledWrapper>
  )
}

export default DropdownBtn

const StyledWrapper = styled.div`
  position: relative;
  .more-menu-btn {
    width: 28px;
    height: 28px;
    .dot {
      width: 3.5px;
      height: 3.5px;
    }
  }
  .more-menu-list {
    z-index: 2;
    display: none;
    position: absolute;
    top: calc(100% + 5px);
    right: 0;
    border: 1px solid #cccccc;
    box-shadow: 0 3px 4px 0 rgba(0, 0, 0, 0.16);
    border-radius: 5px;
    padding: 6px 0;
    text-align: center;
    white-space: nowrap;
    font-size: 12px;
    background-color: white;
    .more-menu {
      border: none;
      background: none;
      display: block;
      padding: 4px 12px;
      &:hover {
        background-color: #f8f8f8;
      }
    }
    &[data-open='true'] {
      display: block;
    }
  }
`
